import { Loader2, Search, X } from "lucide-react"

interface ParcelSearchFormProps {
  searchMode: "reference" | "surface"
  onModeSwitch: (mode: "reference" | "surface") => void
  searchValue: string
  onSearchChange: (value: string) => void
  onClearSearch: () => void
  isSearching: boolean
  onKeyDown: (e: React.KeyboardEvent) => void
  inputRef: React.RefObject<HTMLInputElement | null>
  surfaceMin: string
  surfaceMax: string
  onSurfaceMinChange: (value: string) => void
  onSurfaceMaxChange: (value: string) => void
  ownerType: string
  onOwnerTypeChange: (type: string) => void
  siren: string
  onSirenChange: (value: string) => void
}

export const ParcelSearchForm = ({
  searchMode,
  onModeSwitch,
  searchValue,
  onSearchChange,
  onClearSearch,
  isSearching,
  onKeyDown,
  inputRef,
  surfaceMin,
  surfaceMax,
  onSurfaceMinChange,
  onSurfaceMaxChange,
  ownerType,
  onOwnerTypeChange,
  siren,
  onSirenChange,
}: ParcelSearchFormProps) => {
  return (
    <div data-tour-step="parcel-search" className="p-2.5 shrink-0">
      {/* Segmented control toggle */}
      <div className="text-xs font-medium text-gray-400 mb-1.5">Recherche parcelle</div>
      <div className="flex bg-gray-100 rounded-lg p-0.5">
        <button
          data-tour-step="parcel-search-reference"
          onClick={() => onModeSwitch("reference")}
          className={`flex-1 text-sm font-medium py-1.5 rounded-md transition-all ${
            searchMode === "reference"
              ? "bg-white text-primary shadow-sm"
              : "text-gray-400 hover:text-gray-600 cursor-pointer"
          }`}
        >
          Reference
        </button>
        <button
          data-tour-step="parcel-search-carte"
          onClick={() => onModeSwitch("surface")}
          className={`flex-1 text-sm font-medium py-1.5 rounded-md transition-all ${
            searchMode === "surface"
              ? "bg-white text-primary shadow-sm"
              : "text-gray-400 hover:text-gray-600 cursor-pointer"
          }`}
        >
          Carte
        </button>
      </div>

      {/* Reference mode: code search */}
      {searchMode === "reference" && (
        <div className="mt-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              data-parcel-search
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ex : AB 0052 ou 940280B0052..."
              className="w-full pl-9 pr-9 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-11"
            />
            {isSearching ? (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : searchValue ? (
              <button
                onClick={onClearSearch}
                className="cursor-pointer absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            ) : null}
          </div>
        </div>
      )}

      {/* Surface mode: filter inputs */}
      {searchMode === "surface" && (
        <div className="mt-2 space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <input
                type="number"
                value={surfaceMin}
                onChange={(e) => onSurfaceMinChange(e.target.value)}
                placeholder="Min"
                className="w-full pl-2 pr-8 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-11"
              />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                m{"\u00B2"}
              </span>
            </div>
            <span className="text-gray-300 text-sm">{"\u2014"}</span>
            <div className="relative flex-1 min-w-0">
              <input
                type="number"
                value={surfaceMax}
                onChange={(e) => onSurfaceMaxChange(e.target.value)}
                placeholder="Max"
                className="w-full pl-2 pr-8 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-11"
              />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                m{"\u00B2"}
              </span>
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400 mb-1">Proprietaire</div>
            <div className="flex gap-1.5">
              {(["Tous", "Prive", "Public"] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => onOwnerTypeChange(type)}
                  className={`flex-1 text-sm font-medium py-1.5 rounded-lg transition-all ${
                    ownerType === type
                      ? "bg-primary text-white shadow-sm"
                      : "bg-gray-100 text-gray-500 hover:bg-gray-200 cursor-pointer"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400 mb-1">SIREN</div>
            <div className="relative">
              <input
                type="text"
                value={siren}
                onChange={(e) => onSirenChange(e.target.value)}
                placeholder="Ex : 123456789"
                className="w-full pl-2 pr-8 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-11"
              />
              {siren && (
                <button
                  onClick={() => onSirenChange("")}
                  className="cursor-pointer absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
